import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface JsonViewerProps {
  value: unknown;
  className?: string;
  defaultOpen?: boolean;
  rootName?: string;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function PrimitiveDisplay({ value }: { value: unknown }) {
  if (value === null) return <span className="text-muted-foreground">null</span>;
  if (value === undefined) return <span className="text-muted-foreground">undefined</span>;
  if (typeof value === "string") return <span className="text-emerald-300">"{value}"</span>;
  if (typeof value === "number") return <span className="text-amber-300">{value}</span>;
  if (typeof value === "boolean") return <span className="text-sky-300">{String(value)}</span>;
  return <span>{String(value)}</span>;
}

function Node({ name, value, depth }: { name?: string; value: unknown; depth: number }) {
  const [open, setOpen] = useState(depth < 1);
  const indent = { paddingLeft: depth * 12 };

  if (Array.isArray(value)) {
    return (
      <div style={indent}>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-left text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {name && <span className="text-foreground/80">{name}:</span>}
          <span>Array({value.length})</span>
        </button>
        {open && (
          <div className="mt-0.5">
            {value.map((v, i) => (
              <Node key={i} name={String(i)} value={v} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isObject(value)) {
    const keys = Object.keys(value);
    return (
      <div style={indent}>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-left text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {name && <span className="text-foreground/80">{name}:</span>}
          <span>{`{${keys.length}}`}</span>
        </button>
        {open && (
          <div className="mt-0.5">
            {keys.map((k) => (
              <Node key={k} name={k} value={value[k]} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={indent} className="flex items-baseline gap-2">
      {name && <span className="text-foreground/80">{name}:</span>}
      <PrimitiveDisplay value={value} />
    </div>
  );
}

export function JsonViewer({ value, className, rootName }: JsonViewerProps) {
  if (value == null) {
    return <p className={cn("font-mono text-xs text-muted-foreground", className)}>null</p>;
  }
  return (
    <div className={cn("rounded-md border border-border/50 bg-background/40 p-3 font-mono text-xs leading-relaxed", className)}>
      <Node name={rootName} value={value} depth={0} />
    </div>
  );
}
